// Phone sizing helpers for the ui primitives.
//
// Primitives only ever add `max-md:` classes, so desktop is untouched. They
// also back off when the caller has sized the element itself (h-7, size-6,
// text-xs…): a compact control that a screen sized on purpose keeps its size
// on phones, and only gets the invisible 44px hit area below.

const hasToken = (className: string | undefined, pattern: string) =>
	!!className && new RegExp(`(?:^|\\s)!?(?:${pattern})(?=\\s|$)`).test(className);

/** The caller set an unprefixed height (h-8, h-full, size-6). */
export const hasOwnHeight = (className?: string) => hasToken(className, '(?:h|size)-\\S+');

/** The caller set an unprefixed width (w-8, w-full, size-6). */
export const hasOwnWidth = (className?: string) => hasToken(className, '(?:w|size)-\\S+');

/** The caller positioned the element (absolute, fixed, sticky or relative). */
export const isPositioned = (className?: string) => hasToken(className, 'absolute|fixed|sticky|relative');

/** The caller set a font size (text-xs, text-[15px]), not a colour. */
export const hasOwnTextSize = (className?: string) =>
	hasToken(className, 'text-(?:xs|sm|base|lg|xl|[2-9]xl|\\[\\d[^\\]]*\\])');

/** A font size under 16px, which makes iOS Safari zoom into a focused field. */
export const hasSmallTextSize = (className?: string) =>
	hasToken(className, 'text-(?:xs|sm|\\[(?:1[0-5]|\\d)(?:\\.\\d+)?px\\]|\\[0?\\.\\d+rem\\])');

/** The caller already uses the ::after pseudo-element for something. */
export const usesAfter = (className?: string) => !!className && /(?:^|\s|:)after:/.test(className);

/**
 * An invisible box of at least 44×44px centred on the element, so a small
 * control is still easy to hit with a thumb. The element must be positioned;
 * `touchHitArea` adds `relative` when the caller hasn't positioned it.
 */
export const HIT_AREA_CLASS =
	'max-md:after:absolute max-md:after:left-1/2 max-md:after:top-1/2 max-md:after:h-11 max-md:after:min-h-full max-md:after:w-11 max-md:after:min-w-full max-md:after:-translate-x-1/2 max-md:after:-translate-y-1/2';

/**
 * HIT_AREA_CLASS on every touch screen (a coarse pointer), whatever its width:
 * tablets and landscape phones use the desktop layout but still need 44px
 * targets. A mouse never matches, so desktop is untouched.
 */
export const COARSE_HIT_AREA_CLASS =
	'touch:after:absolute touch:after:left-1/2 touch:after:top-1/2 touch:after:h-11 touch:after:min-h-full touch:after:w-11 touch:after:min-w-full touch:after:-translate-x-1/2 touch:after:-translate-y-1/2';

/**
 * HIT_AREA_CLASS plus `relative` when needed, or nothing when ::after is taken.
 * `coarse` also adds it on touch screens of any width (COARSE_HIT_AREA_CLASS).
 */
export const touchHitArea = (className?: string, { coarse = false } = {}) => {
	if (usesAfter(className)) return '';
	if (!coarse) return isPositioned(className) ? HIT_AREA_CLASS : `max-md:relative ${HIT_AREA_CLASS}`;
	const area = `${HIT_AREA_CLASS} ${COARSE_HIT_AREA_CLASS}`;
	return isPositioned(className) ? area : `max-md:relative touch:relative ${area}`;
};
